import React, { useState } from 'react';
import { X, Mail, Check, Sparkles, Ticket, Zap, Gift } from 'lucide-react';
import { apiFetch } from '../utils/api';

const NewsletterModal = ({ open, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [termsConsent, setTermsConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!firstName.trim()) {
      setError('Please enter your first name');
      setLoading(false);
      return;
    }

    if (!lastName.trim()) {
      setError('Please enter your last name');
      setLoading(false);
      return;
    }

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    if (!termsConsent) {
      setError('Please agree to the Privacy Policy and Terms & Conditions');
      setLoading(false);
      return;
    }

    if (!marketingConsent) {
      setError('Please consent to receive marketing emails to join the newsletter');
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch(`/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          name: `${firstName} ${lastName}`.trim()
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFirstName('');
          setLastName('');
          setEmail('');
          setTermsConsent(false);
          setMarketingConsent(false);
          onClose();
        }, 2000);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Unable to connect. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] rounded-lg max-w-md w-full p-6 md:p-8 relative border border-[#E10600]/20 max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-[#E10600] transition-colors"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <Mail className="w-12 h-12 text-[#E10600]" />
              </div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Join Our <span className="thriller-text">Newsletter</span>
              </h2>
              <p className="text-gray-400 text-center">
                Get exclusive PopCrown news, app updates, and special offers delivered to your inbox.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields - Side by Side */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                    className="newsletter-input w-full"
                    required
                  />
                </div>
                <div className="flex-1">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last Name"
                    className="newsletter-input w-full"
                    required
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="newsletter-input w-full"
                  required
                />
              </div>

              {/* Benefits List */}
              <div className="bg-[#1a1a1a]/50 p-4 rounded-lg">
                <p className="text-[#E10600] font-semibold mb-2 text-sm inline-flex items-center gap-2"><Sparkles className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" /> VIP Subscriber Benefits:</p>
                <ul className="space-y-2 text-xs text-gray-400">
                  <li className="flex items-start gap-2"><Ticket className="w-3.5 h-3.5 text-[#E10600] mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" /> Exclusive discount codes for app, events &amp; store</li>
                  <li className="flex items-start gap-2"><Zap className="w-3.5 h-3.5 text-[#E10600] mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" /> Early access to new features and announcements</li>
                  <li className="flex items-start gap-2"><Gift className="w-3.5 h-3.5 text-[#E10600] mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" /> Special offers and surprises for our community</li>
                </ul>
              </div>

              {/* T&Cs Consent (Required) */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="termsConsentNewsletter"
                  checked={termsConsent}
                  onChange={(e) => setTermsConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#E10600] cursor-pointer"
                  required
                />
                <label htmlFor="termsConsentNewsletter" className="text-sm text-gray-400 cursor-pointer">
                  I agree to the{' '}
                  <a href="/privacy" className="text-[#E10600] hover:underline">
                    Privacy Policy
                  </a>
                  {' '}and{' '}
                  <a href="/terms" className="text-[#E10600] hover:underline">
                    Terms & Conditions
                  </a>. *
                </label>
              </div>

              {/* Marketing Consent (Required) */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="marketingConsentNewsletter"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#E10600] cursor-pointer"
                  required
                />
                <label htmlFor="marketingConsentNewsletter" className="text-sm text-gray-400 cursor-pointer">
                  I consent to receive marketing emails, news, and exclusive offers from PopCrown Official Ltd. *
                </label>
              </div>

              {error && (
                <div className="text-red-500 text-sm">{error}</div>
              )}

              <button 
                type="submit" 
                disabled={loading || !termsConsent || !marketingConsent}
                className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                aria-label="Subscribe to newsletter"
              >
                {loading ? 'Subscribing...' : 'Subscribe Now'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-[#E10600] rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-black" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-gray-400">You've successfully subscribed to our newsletter.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterModal;
